import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ImageUp, Trash2 } from 'lucide-react'
import { useRef, useState } from 'react'

import { Alert, Button } from '@/components/layout/ui'
import { v2Api } from '@/features/issue'
import { queryKeys } from '@/lib/queryClient'
import { messageFor } from '@/lib/errors'
import { useWorkspace } from '@/lib/workspace'

import { ProjectIconTile } from './ProjectIconPicker'

/**
 * Upload a logo for a project.
 *
 * The logo REPLACES the symbol in the web app but does not erase it: the stored
 * icon is what the iPad draws, since SF Symbols are rendered natively there and
 * it has no concept of an uploaded file. Removing a logo therefore falls back to
 * the symbol rather than leaving the project with no mark at all — which is
 * also why the icon and colour pickers stay visible and usable while a logo is
 * set, rather than being disabled as redundant.
 */
export function LogoUpload({
  projectKey,
  icon,
  color,
  hasLogo,
  canEdit,
}: {
  projectKey: string
  icon: string
  color: string
  hasLogo: boolean
  canEdit: boolean
}) {
  const workspace = useWorkspace()
  const client = useQueryClient()
  const input = useRef<HTMLInputElement>(null)
  const [tooBig, setTooBig] = useState<string | null>(null)

  // Cache-busted on every change. The URL is stable — it is the project's own
  // /logo route — so without this the browser keeps showing the old image after
  // an upload, and it looks as though nothing happened.
  const [version, setVersion] = useState(0)
  const src = hasLogo
    ? `${v2Api.projectLogoUrl(workspace.slug, projectKey)}?v=${version}`
    : null

  const refresh = async () => {
    setVersion((n) => n + 1)
    await client.invalidateQueries({ queryKey: queryKeys.orgProjects(workspace.slug) })
    await client.invalidateQueries({ queryKey: queryKeys.projects })
  }

  const upload = useMutation({
    mutationFn: (file: File) => v2Api.uploadProjectLogo(workspace.slug, projectKey, file),
    onSuccess: refresh,
  })
  const remove = useMutation({
    mutationFn: () => v2Api.deleteProjectLogo(workspace.slug, projectKey),
    onSuccess: refresh,
  })

  const pending = upload.isPending || remove.isPending
  // Matches the server's limit. Checked here too so a 3 MB photo fails
  // instantly instead of after being uploaded and refused.
  const MAX_BYTES = 2 * 1024 * 1024

  return (
    <div className="text-xs font-medium text-ink-600">
      <span className="mb-1 block">Logo</span>

      <div className="flex items-center gap-3">
        <ProjectIconTile icon={icon} color={color} size="md" logoUrl={src} />

        <div className="flex flex-wrap items-center gap-2">
          <input
            ref={input}
            type="file"
            // The server decides what a file is by reading it; this only stops
            // the file picker offering things that are certain to be refused.
            accept="image/png,image/jpeg,image/gif,image/webp"
            className="sr-only"
            onChange={(event) => {
              const file = event.target.files?.[0]
              event.target.value = '' // so re-picking the same file still fires
              if (!file) return
              if (file.size > MAX_BYTES) {
                setTooBig(
                  `That image is ${Math.round(file.size / 1024)} KB. The limit is ${MAX_BYTES / 1024} KB.`,
                )
                return
              }
              setTooBig(null)
              upload.mutate(file)
            }}
          />
          <Button
            size="sm"
            variant="ghost"
            disabled={!canEdit || pending}
            onClick={() => input.current?.click()}
          >
            <ImageUp className="size-4" aria-hidden="true" />
            {upload.isPending ? 'Uploading…' : hasLogo ? 'Replace' : 'Upload'}
          </Button>
          {hasLogo && (
            <Button
              size="sm"
              variant="ghost"
              disabled={!canEdit || pending}
              onClick={() => remove.mutate()}
            >
              <Trash2 className="size-4" aria-hidden="true" />
              {remove.isPending ? 'Removing…' : 'Remove'}
            </Button>
          )}
        </div>
      </div>

      <p className="mt-1.5 font-normal text-ink-500">
        PNG, JPEG, GIF or WebP, up to 2&nbsp;MB. A logo replaces the icon in the web app; the iPad
        keeps showing the icon, so it is worth setting both.
      </p>

      {tooBig && (
        <div className="mt-2">
          <Alert tone="danger">{tooBig}</Alert>
        </div>
      )}
      {upload.isError && (
        <div className="mt-2">
          <Alert tone="danger">{messageFor(upload.error)}</Alert>
        </div>
      )}
      {remove.isError && (
        <div className="mt-2">
          <Alert tone="danger">{messageFor(remove.error)}</Alert>
        </div>
      )}
    </div>
  )
}
